import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, Lock, Eye, Database } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button variant="ghost" className="text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <div className="h-6 w-px bg-white/20"></div>
            <h1 className="text-xl font-brand font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card className="glass-premium">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
                <p className="text-white/70">How we protect and handle your data</p>
              </div>
            </div>

            <div className="prose prose-invert max-w-none space-y-8 text-white/80 leading-relaxed">
              <section>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 mb-6">
                  <div className="flex items-start space-x-3">
                    <Shield className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-green-300 mb-2">Your Privacy Matters</h3>
                      <p className="text-green-200/80">
                        usexxx is committed to protecting your privacy. This policy explains how we collect, 
                        use, and safeguard your information when you use our service.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Database className="h-5 w-5 mr-2 text-blue-400" />
                  1. Information We Collect
                </h2>
                
                <h3 className="text-lg font-semibold text-white mb-3">Automatically Collected Information:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
                  <li><strong>Usage Data:</strong> Pages visited, time spent, click patterns, search queries</li>
                  <li><strong>IP Address:</strong> For security, analytics, and content delivery</li>
                  <li><strong>Cookies:</strong> For preferences, authentication, and site functionality</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mb-3 mt-6">Information You Provide:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Age Verification:</strong> Confirmation that you are 18 or older</li>
                  <li><strong>Search Queries:</strong> To provide relevant content recommendations</li>
                  <li><strong>Preferences:</strong> Video categories, quality settings, language preferences</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Eye className="h-5 w-5 mr-2 text-purple-400" />
                  2. How We Use Your Information
                </h2>
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-6">
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Service Delivery:</strong> Provide and improve our streaming service</li>
                    <li><strong>Personalization:</strong> Recommend content based on your interests</li>
                    <li><strong>Security:</strong> Detect fraud, abuse, and protect against threats</li>
                    <li><strong>Analytics:</strong> Understand usage patterns to improve our service</li>
                    <li><strong>Legal Compliance:</strong> Meet legal obligations and enforce our terms</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. Cookies and Tracking</h2>
                <p>We use cookies and similar technologies to enhance your experience:</p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-300 mb-2">Essential Cookies</h4>
                    <p className="text-sm">Required for basic site functionality, age verification, and security.</p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-300 mb-2">Analytics Cookies</h4>
                    <p className="text-sm">Help us understand how you use our site to improve performance.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. Data Sharing & Third Parties</h2>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-red-300 mb-3">We DO NOT:</h3>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Sell your personal information to anyone</li>
                    <li>Share your viewing history with third parties</li>
                    <li>Store payment information (handled by secure processors)</li>
                    <li>Track you across other websites without consent</li>
                  </ul>
                </div>

                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6 mt-4">
                  <h3 className="text-lg font-semibold text-green-300 mb-3">We MAY Share Data With:</h3>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Service Providers:</strong> Hosting, analytics, and security services</li>
                    <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                    <li><strong>Business Transfers:</strong> In case of merger, acquisition, or asset sale</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
                <p>We implement industry-standard security measures to protect your information:</p>
                <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                  <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
                  <li><strong>Access Controls:</strong> Limited employee access on a need-to-know basis</li>
                  <li><strong>Regular Audits:</strong> Security reviews and vulnerability assessments</li>
                  <li><strong>Secure Infrastructure:</strong> Protected servers and secure data centers</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Your Rights & Choices</h2>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-300 mb-3">You Have the Right To:</h3>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Access:</strong> Request a copy of your personal data</li>
                    <li><strong>Correct:</strong> Update or correct inaccurate information</li>
                    <li><strong>Delete:</strong> Request deletion of your personal data</li>
                    <li><strong>Object:</strong> Opt-out of certain data processing activities</li>
                    <li><strong>Portability:</strong> Receive your data in a structured format</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Data Retention</h2>
                <p>
                  We retain your information only as long as necessary to provide our services and comply with legal obligations:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                  <li><strong>Usage Data:</strong> 2 years for analytics and improvement purposes</li>
                  <li><strong>Age Verification:</strong> Retained until you clear browser data</li>
                  <li><strong>Security Logs:</strong> 1 year for security and fraud prevention</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. International Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries other than your own. 
                  We ensure appropriate safeguards are in place to protect your data during international transfers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. Contact Us</h2>
                <div className="bg-pink-500/10 border border-pink-500/20 rounded-lg p-6">
                  <p className="text-pink-300">
                    <strong>Privacy Questions or Requests:</strong><br />
                    Email: privacy@usexxx.com<br />
                    Subject: Privacy Policy Inquiry<br />
                    Response time: 30 days maximum
                  </p>
                </div>
              </section>

              <div className="border-t border-white/10 pt-8 mt-12">
                <p className="text-sm text-white/60">
                  <strong>Last Updated:</strong> January 2024<br />
                  <strong>Effective Date:</strong> Immediately upon posting<br />
                  <strong>Policy Changes:</strong> We will notify users of material changes via email or site notice.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;